import { Component, ErrorInfo, ReactNode } from 'react'

import * as Sentry from '@sentry/nextjs'

import { IncidentPlayerError } from '../types/types'
import { VideoChunkSurface } from './VideoChunkSurface'
import { VideoError } from './VideoError'

interface VideoChunkBoundaryIProps {
  children: ReactNode
  placeholderHeight?: string
  placeholderWidth?: string
  borderRadius?: string
}

interface VideoChunkBoundaryStateI {
  hasChunkError: boolean
}

/**
 * Error boundary for a lazily-imported player chunk.
 *
 * This app has no error boundary of its own (`.claude/rules/error-handling.md`),
 * so a render throw unmounts up to Next's boundary and drops the operator on
 * `_error`. A rejected `import()` is a real way to get one: web-app is a static
 * export served from S3/CloudFront, so after a `release_webapp` deploy the
 * previous build's chunk filenames stop resolving for anyone still holding the
 * page open. Without this, opening a Lumana camera drawer at that moment costs
 * the whole Devices page instead of one video tile, and nothing reaches Sentry.
 *
 * `React.lazy` caches the rejected promise, so the tile stays in its error state
 * until the page is reloaded — which is also what fixes it, since a reload
 * fetches the new chunk names.
 */
export class VideoChunkBoundary extends Component<
  VideoChunkBoundaryIProps,
  VideoChunkBoundaryStateI
> {
  state: VideoChunkBoundaryStateI = { hasChunkError: false }

  static getDerivedStateFromError(): VideoChunkBoundaryStateI {
    return { hasChunkError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        feature: 'video_player',
        component: 'LiveKitVideoPlayer',
        action: 'livekit_chunk_load',
      },
      extra: { componentStack: errorInfo.componentStack },
    })
  }

  render() {
    const { children, placeholderHeight, placeholderWidth, borderRadius } =
      this.props

    if (this.state.hasChunkError) {
      return (
        <VideoChunkSurface
          borderRadius={borderRadius}
          placeholderHeight={placeholderHeight}
          placeholderWidth={placeholderWidth}
        >
          <VideoError errorLabel={IncidentPlayerError.ERROR_FETCHING_STREAM} />
        </VideoChunkSurface>
      )
    }

    return children
  }
}
